export const experience = [
  {
    "title": "Software Engineer",
    "organization": "Databricks",
    "location": "United States",
    "period": "Nov 2025 – Present",
    "bullets": [
      "Architected and scaled distributed data and AI platform services using Python, PySpark, FastAPI, and Delta Lake.",
      "Worked on low-latency LLM serving and model-monitoring pipelines using MLflow, LangChain, and REST APIs.",
      "Optimized data access and caching using PostgreSQL, Delta Lake indexing, and Redis for concurrent enterprise workloads."
    ],
    "technologies": [
      "Python",
      "PySpark",
      "FastAPI",
      "Delta Lake",
      "PostgreSQL",
      "Redis",
      "MLflow",
      "LangChain",
      "REST APIs",
      "LLMs",
      "Distributed Systems"
    ]
  },
  {
    "title": "Software Engineer Intern",
    "organization": "SnapLogic",
    "location": "San Mateo, CA",
    "period": "May 2025 – Nov 2025",
    "bullets": [
      "Developed the HL7 Snap Pack using Java 11 and the Snap Development Kit.",
      "Implemented parser logic for converting HL7 v2.x healthcare messages into structured JSON and formatter logic for reconstructing standards-compliant HL7 messages.",
      "Implemented schema validation, delimiter and encoding handling, fault-tolerant error views, and automated JUnit unit and integration testing.",
      "Worked with Maven and Travis CI and improved Snap execution reliability across Groundplex and Cloudplex environments."
    ],
    "technologies": [
      "Java 11",
      "HL7 v2.x",
      "Snap Development Kit",
      "JSON",
      "JUnit",
      "Maven",
      "Travis CI",
      "Groundplex",
      "Cloudplex",
      "CI/CD"
    ]
  },
  {
    "title": "Software Engineer Applications",
    "organization": "ZS Associates",
    "location": "Pune, India",
    "period": "Jan 2022 – Jul 2023",
    "bullets": [
      "Built and maintained Java backend services and AngularJS dashboards for ICOPs, a global sales compensation platform supporting payout and payroll workflows.",
      "Developed and optimized PostgreSQL queries and backend services for reporting, compensation, and performance dashboards.",
      "Integrated Informatica ETL data into Java backend APIs.",
      "Implemented validation and ingestion logic to ensure schema compatibility and accurate outputs across multiple enterprise data sources."
    ],
    "technologies": [
      "Java",
      "AngularJS",
      "PostgreSQL",
      "Informatica",
      "ETL",
      "REST APIs",
      "TeamCity",
      "SQL",
      "Backend Development"
    ]
  },
  {
    "title": "Software Engineer",
    "organization": "BrownFrame Technologies",
    "location": "India",
    "period": "Jan 2021 – Dec 2021",
    "bullets": [
      "Developed scalable backend services and REST APIs using Java, Spring Boot, and Python.",
      "Designed and optimized MySQL schemas and database indexes.",
      "Created and standardized API documentation using Swagger, OpenAPI, and Postman."
    ],
    "technologies": [
      "Java",
      "Spring Boot",
      "Python",
      "MySQL",
      "REST APIs",
      "Swagger",
      "OpenAPI",
      "Postman"
    ]
  }
];

export const publications = [
  {
    title: "Reconciling DORA Metrics for Educational Projects on GitHub",
    venue: "IEEE CSEE&T",
    linkUrl: "https://www.linkedin.com/posts/kevin-buffardi-5a84351_congrats-to-aviral-kumar-srivastava-ms-in-activity-7459468158286946304-XUvy?utm_source=share&utm_medium=member_desktop&rcm=ACoAAA38K5cBe08aM4jtReayfR6p126TRc9UgLU",
    year: "2026",
    summary:
      "Adapts industry-grade DevOps productivity metrics for student software engineering projects on GitHub.",
  },
  {
    title:
      "Chronicling Consistency and Parity of Contributions to Software Engineering Team Projects",
    venue: "ACM ITiCSE",
    year: "2025",
    summary:
      "Examines contribution consistency and parity across undergraduate software engineering teams using GitHub activity.",
    linkLabel: "Publication",
    linkUrl: "https://dl.acm.org/doi/10.1145/3724363.3729039",
  },
  {
    title:
      "An IoT-Based Approach in Automatic Garbage Segregation",
    venue: "Springer",
    summary:
      "Presents an intelligent waste-segregation approach that connects IoT concepts with sustainable engineering.",
    linkLabel: "Publication",
    linkUrl: "https://link.springer.com/chapter/10.1007/978-981-16-8546-0_6",
  },
];

export const researchRoles = [
  {
    title: "CREATE Research Assistant",
    organization: "CSU Chico CREATE Research with Professor Kevin Buffardi",
    period: "Current",
    summary:
      "Creating tools that help students learn artificial intelligence concepts in software engineering education.",
    linkLabel: "Research group",
    linkUrl: "https://learnbyfailure.com/research/",
  },
  {
    title: "Research Assistant",
    organization: "CSU Chico Software Engineering Education Research",
    period: "September 2024 - May 2025",
    summary:
      "Studied how contribution patterns affect team cohesiveness and conflict through seven years of GitHub repository data.",
    linkLabel: "Related publication",
    linkUrl: "https://dl.acm.org/doi/10.1145/3724363.3729039",
  },
  {
    title: "Research Assistant",
    organization: "CSU Chico Machine Learning Research",
    period: "September 2024 - March 2025",
    summary:
      "Built RNN and CNN models that use acoustic sensor fusion to support Central African forest elephant conservation.",
  },
];

export const projects = [
  {
    title: "OurTasks",
    subtitle: "Shared checklist workspace",
    year: "2026",
    category: "Product",
    technologies: "Next.js, Netlify, realtime collaboration",
    accent: "#ee7567",
    visual: "checklist",
    summary:
      "A no-account shared checklist workspace where anyone can create a list, share one link, and collaborate in real time.",
    problem: "Shared checklists usually introduce account setup before a group can do any useful work.",
    decision: "Make the shareable link the workspace identity so participation can begin immediately.",
    implementation: "Built the product in Next.js and deployed it on Netlify with realtime collaboration at the center of the interaction model.",
    outcome: "Anyone can create, share, and update the same checklist without creating an account.",
    metric: "Realtime collaboration",
    featured: true,
    liveLink: "https://otasks.netlify.app/",
  },
  {
    title: "Personal Portfolio",
    subtitle: "A living professional site",
    year: "2025",
    category: "Web",
    technologies: "React, Netlify, CSS",
    accent: "#26a6a3",
    visual: "signal",
    summary:
      "A responsive portfolio that combines engineering work, research, and product projects into one clear narrative.",
    problem: "Professional experience, research, and product work were fragmented across conventional portfolio sections.",
    decision: "Use one scroll-driven planetary journey to connect the evidence while keeping content readable and accessible.",
    implementation: "Built with React, Three.js, semantic HTML, responsive CSS, Netlify delivery, and adaptive motion fallbacks.",
    outcome: "A single responsive narrative now presents engineering work, research, projects, and recommendations together.",
    metric: "Responsive experience",
    featured: true,
    liveLink: "https://aviralkumarsrivastava.netlify.app/",
    github: "https://github.com/aviraw/Portfolio",
  },
  {
    title: "Snap & Cook",
    subtitle: "AI recipe generator",
    year: "2025",
    category: "AI",
    technologies: "Next.js, Firebase, LLM APIs",
    accent: "#c99840",
    visual: "recipe",
    summary:
      "Built an AI-powered recipe application that generates personalized recipes from ingredient images and text, integrates nutrition data and external APIs, and uses caching to reduce unnecessary API calls.",
    problem: "Repeated recipe requests made model calls unnecessarily expensive and slow.",
    decision: "Persist and reuse generated results instead of treating every request as new.",
    implementation: "Combined a Next.js interface, LLM APIs, nutrition data, and Firebase-backed persistence and caching.",
    outcome: "Caching reduces unnecessary API calls while keeping generated recipes available.",
    metric: "Recipe caching",
    featured: true,
    liveLink: "https://snap-and-cook-z2rb.vercel.app/",
    github: "https://github.com/aviraw/Snap-and-Cook",
  },
  {
    title: "QuickQuery",
    subtitle: "RAG Document QA",
    year: "2024",
    category: "RAG",
    technologies: "React, Django REST Framework, Docker, GCP",
    accent: "#716cb5",
    visual: "query",
    summary:
      "Built a RAG-based document question-answering system with document ingestion, semantic retrieval, context-grounded responses, containerization, and GCP deployment.",
    problem: "Finding a specific answer inside long documents requires more than keyword search.",
    decision: "Ground responses in retrieved document context through a dedicated RAG workflow.",
    implementation: "Connected a React client to Django REST services, containerized the system with Docker, and deployed it on Google Cloud Run.",
    outcome: "The complete document-query workflow runs as a cloud-deployed application.",
    metric: "Cloud deployed",
    articleLink: "https://medium.com/@aviral23.1999/quick-query-ad25db076926",
    featured: true,
    github: "https://github.com/QuickQueryQQ/QuickQuery",
  },
  {
    title: "Smart Home Automation",
    subtitle: "Voice-controlled IoT system",
    year: "2022",
    category: "IoT",
    technologies: "NodeMCU, Arduino IoT, Alexa",
    accent: "#4c9a78",
    visual: "home",
    summary:
      "An Alexa-controlled system that automated more than eight home devices and reduced manual operation by 90%.",
    problem: "Controlling multiple home devices individually created repetitive manual work.",
    decision: "Expose the device layer through one voice-controlled interface.",
    implementation: "Integrated NodeMCU hardware, Arduino IoT services, and Alexa commands across more than eight devices.",
    outcome: "The system reduced manual device operation by 90%.",
    metric: "90% less manual operation",
    github: "https://github.com/aviraw/Home_Automation",
  },
  {
    title: "Enigma",
    subtitle: "File security system",
    year: "2020",
    category: "Security",
    technologies: "C++, Android, AES-128",
    accent: "#4b8bc8",
    visual: "shield",
    summary:
      "An AES-128 file-encryption tool with a companion Android app for secure QR-based decryption.",
    problem: "Encrypted file transfer still needs a practical way to move decryption context between devices.",
    decision: "Pair desktop encryption with a companion mobile QR-based decryption flow.",
    implementation: "Built AES-128 file encryption in C++ and connected it to an Android companion application.",
    outcome: "Files can be encrypted on one device and securely decrypted through the companion workflow.",
    metric: "AES-128 encryption",
    github: "https://github.com/aviraw/Minor1---Enigma",
  },
];

export const skillGroups = [
  {
    "title": "Languages",
    "skills": [
      "Java",
      "Python",
      "JavaScript",
      "TypeScript",
      "C++",
      "SQL",
      "HTML",
      "CSS"
    ]
  },
  {
    "title": "Backend / Architecture",
    "skills": [
      "Spring Boot",
      "Django",
      "FastAPI",
      "Node.js",
      "REST APIs",
      "gRPC",
      "GraphQL",
      "Microservices",
      "Distributed Systems",
      "Kafka",
      "RabbitMQ"
    ]
  },
  {
    "title": "Frontend",
    "skills": [
      "React.js",
      "Next.js",
      "AngularJS",
      "Redux Toolkit",
      "Tailwind CSS"
    ]
  },
  {
    "title": "AI / ML",
    "skills": [
      "LLM Integration",
      "LangChain",
      "OpenAI APIs",
      "Claude APIs",
      "RAG",
      "Semantic Search",
      "Prompt Engineering",
      "MLflow"
    ]
  },
  {
    "title": "Data / Cloud",
    "skills": [
      "PostgreSQL",
      "MySQL",
      "MongoDB",
      "Redis",
      "Delta Lake",
      "AWS",
      "Azure",
      "GCP"
    ]
  },
  {
    "title": "DevOps / Testing",
    "skills": [
      "Docker",
      "Kubernetes",
      "Terraform",
      "Git",
      "Maven",
      "Travis CI",
      "Jenkins",
      "GitHub Actions",
      "JUnit",
      "PyTest",
      "Jest",
      "Postman"
    ]
  }
];
